/**
 * 적응형 화질.
 *
 * PC 사양은 알 수 없고 유저는 설정 화면을 뒤지지 않는다. 그래서 프레임 시간을 계속 재서
 * 목표(60fps)를 못 맞추면 **비싼 것부터 순서대로** 내린다.
 *
 * 내리는 순서는 비용 크기 순이다:
 *   1) 렌더 해상도  — 픽셀 비용이 지배적일 때 가장 효과가 크고 형태는 그대로
 *   2) GTAO         — 접촉 그림자. 없어도 게임은 읽힌다
 *   3) 포스트 전체  — 블룸/그레이딩까지 끄고 직접 렌더
 *
 * 올릴 때는 느리게, 그리고 오르내림이 한 번 시작되면 그 자리에서 고정한다
 * (오르내림이 반복되는 게 낮은 화질로 고정된 것보다 훨씬 거슬린다).
 */

import type { Quality } from './pipeline';

export interface Step {
  quality: Quality;
  /** 렌더할 픽셀 수 상한 (가로 × 세로). DPR 이 아니다 — ratioFor 주석 참고 */
  budgetPx: number;
  label: string;
}

/**
 * 렌더 픽셀 수 예산에서 renderer.setPixelRatio 값을 역산한다.
 *
 * **DPR 상한으로는 렌더 부하를 제어할 수 없다.** 4K 모니터에 배율 100% 면
 * devicePixelRatio 가 1 이라 "DPR 2 이하로 제한" 같은 코드에 아예 걸리지 않는데,
 * 창이 3840×2160 이므로 실제로 그리는 픽셀은 8.3M — 1080p 의 네 배다.
 * 화면 크기와 무관하게 일정한 부하를 유지하려면 픽셀 수를 직접 예산으로 잡고
 * 거기서 비율을 뽑아야 한다.
 *
 * @param maxRatio 절대 상한. 모바일은 DPR 이 3을 넘기도 해서 따로 묶는다.
 */
export function ratioFor(budgetPx: number, maxRatio = Infinity) {
  const cssPx = Math.max(1, window.innerWidth * window.innerHeight);
  const dpr = window.devicePixelRatio || 1;
  // 화면보다 더 촘촘히 그릴 이유는 없으므로 dpr 이 천장이다.
  return Math.max(0.25, Math.min(dpr, maxRatio, Math.sqrt(budgetPx / cssPx)));
}

/** 시작 단계 선택용 — 해상도가 720p 로 고정됐으므로 이 값도 그에 맞춘다 */
export const START_PX = 1280 * 720;

/**
 * **렌더 해상도는 720p 로 고정한다.**
 *
 * 처음에는 540p 였다 — Wayne 이 「강제로 540 으로 해도 게임에 지장 없다」고 판단했다.
 * 그런데 실제로 해 보니 **「게임을 못할 정도로 화질이 안 좋다」** 였다. 단서를 눈으로 읽는
 * 게임이라 글자·질감이 뭉개지면 조사 자체가 안 된다. 720p 가 그 하한이다.
 *
 * 그래도 4K 화면의 8.3M 픽셀을 그리게 두지는 않는다 — 화면 크기와 무관하게 0.92M 로
 * 묶는다. 단계는 **효과(quality)만** 오르내린다(GTAO·블룸부터 끈다). budgetPx 가 전부
 * 같으므로 setPixelRatio 는 사실상 한 번만 바뀐다.
 */
const PX_720 = 1280 * 720;
export const STEPS: Step[] = [
  { quality: 'high', budgetPx: PX_720, label: '높음 (720p)' },
  { quality: 'medium', budgetPx: PX_720, label: '보통 (720p)' },
  { quality: 'low', budgetPx: PX_720, label: '낮음 (720p)' },
];

/** 목표는 60fps. 아래 두 문턱 사이는 '적당함'으로 두어 오르내림을 막는다. */
/** 이 이상 느리면 무겁다고 본다 (약 45fps) */
const SLOW_MS = 1000 / 45;
/** 이 이하로 빠르면 여유가 있다고 본다 (약 75fps) */
const FAST_MS = 1000 / 75;

export class AdaptiveQuality {
  index: number;
  enabled = true;
  /** 이 단계보다 비싼 쪽(작은 index)으로는 다시 올라가지 않는다 */
  private ceiling = 0;
  /** 강등으로 닿은 적 있는 단계. 두 번째로 떨어지면 오르내림이 시작된 것이다 */
  private reached = new Set<number>();
  private slowFor = 0;
  private fastFor = 0;
  private avgMs = -1;
  private settle = 1.2;

  constructor(
    startIndex: number,
    private apply: (step: Step) => void,
    private notify?: (step: Step, direction: 'down' | 'up') => void,
  ) {
    this.index = startIndex;
    this.reached.add(startIndex);
  }

  /**
   * 프레임 상한(main.ts FPS_CAP)이 있으면 프레임 간격이 상한에 붙어 「여유 있음」(75fps 이상)이 영영 안 나온다 —
   * 한 번 내린 화질이 다시 안 올라간다. 상한 간격 근처(+8%)면 여유로 본다
   */
  private capMs = 0;
  setCap(ms: number) { this.capMs = ms; }

  /** 수동으로 화질을 고르면 자동 조절을 끈다 */
  disable() {
    this.enabled = false;
  }

  /** 화질을 바꾼 직후에는 잠시 측정을 무시한다 (전환 프레임이 튄다) */
  private hold() {
    this.settle = 0.8;
    this.slowFor = 0;
    this.fastFor = 0;
  }

  update(dt: number) {
    if (!this.enabled) return;
    if (this.settle > 0) {
      this.settle -= dt;
      return;
    }

    const ms = Math.min(1000, dt * 1000);
    // 첫 측정은 그대로 받는다. 기본값에서 천천히 수렴하면 무거운 기기에서
    // 강등이 몇 십 초씩 늦어진다.
    if (this.avgMs < 0) this.avgMs = ms;
    else this.avgMs += (ms - this.avgMs) * 0.15;

    if (this.avgMs > SLOW_MS) {
      this.slowFor += dt;
      this.fastFor = 0;
    } else if (this.avgMs < Math.max(FAST_MS, this.capMs * 1.08)) {
      this.fastFor += dt;
      this.slowFor = 0;
    } else {
      this.slowFor = Math.max(0, this.slowFor - dt * 0.5);
      this.fastFor = Math.max(0, this.fastFor - dt * 0.5);
    }

    // 무거우면 빠르게 내린다 (1.5초)
    if (this.slowFor > 1.5 && this.index < STEPS.length - 1) {
      this.index++;
      // 이 단계로 **두 번째로** 떨어졌다 = 올렸다가 다시 내려온 것이다. 여기서 굳힌다.
      //
      // 예전 가드는 `index - 1 < this.floor` 였는데 floor 가 Math.max 로 증가만 해서
      // 늘 `index <= floor` 였고, 따라서 조건이 **항상 참**이었다. 결과로
      // 12초 승급 -> 1.5초 강등이 약 15초 주기로 무한 반복됐다. 전환마다
      // Pipeline.build() 가 돌아 렌더타겟 15개를 다시 잡고 GTAO/블룸 셰이더를
      // 다시 컴파일한다 — "가끔 엄청 렉걸린다" 의 정체였다.
      if (this.reached.has(this.index)) this.ceiling = this.index;
      this.reached.add(this.index);
      this.apply(STEPS[this.index]);
      this.notify?.(STEPS[this.index], 'down');
      this.hold();
      return;
    }

    // 여유가 있으면 느리게 올린다 (12초). 굳은 단계 위로는 올라가지 않는다.
    if (this.fastFor > 12 && this.index - 1 >= this.ceiling) {
      this.index--;
      this.apply(STEPS[this.index]);
      this.notify?.(STEPS[this.index], 'up');
      this.hold();
    }
  }

  get current() {
    return STEPS[this.index];
  }

  get fps() {
    return Math.round(1000 / this.avgMs);
  }
}
